'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from 'react';
import type { UIMessage } from 'ai';
import { AI_SEARCH_EVENTS, type AiSearchAnalytics } from './ai-search-analytics';
import { dispatchAssistantTurnAnalytics, type AssistantTurnStatus } from './assistant-turn-analytics';
import { classifyRefusalFromError } from './refusal-classifier';

/**
 * Fires `results_shown` / `no_results` exactly once per assistant turn, on the
 * transition into `'ready'`. Lives in a hook (the guide's accepted escape
 * hatch) so the caller doesn't carry the status-diff ref + dedupe Set inline.
 * The dedupe Set is keyed on assistant message id; the previous-status ref
 * detects the transition.
 */
export function useTrackAssistantTurns(input) {
  const $ = _c(6);
  const previousStatusRef = useRef(input.status);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = new Set();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const trackedAssistantIdsRef = useRef(t0);
  let t1;
  if ($[1] !== input) {
    t1 = () => {
      const previousStatus = previousStatusRef.current;
      previousStatusRef.current = input.status;
      const dispatched = dispatchAssistantTurnAnalytics({
        status: input.status,
        previousStatus,
        messages: input.messages,
        sessionId: input.getSessionId(),
        trackedAssistantIds: trackedAssistantIdsRef.current,
        consumeSubmitContext: input.consumeSubmitContext,
        analytics: input.analytics
      });
      if (dispatched) {
        input.onDispatched();
      }
    };
    $[1] = input;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== input.messages || $[4] !== input.status) {
    t2 = [input.status, input.messages];
    $[3] = input.messages;
    $[4] = input.status;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  useEffect(t1, t2);
}

/**
 * Captures the `REFUSAL` analytics event whenever `useChat.error` flips to a
 * NEW error reference. Same escape-hatch encapsulation as
 * `useTrackAssistantTurns` — the dedupe ref lives in the hook, not in the
 * calling component.
 */
export function useTrackChatErrors(input) {
  const $ = _c(5);
  const trackedErrorRef = useRef(null);
  let t0;
  if ($[0] !== input) {
    t0 = () => {
      if (!input.error) {
        return;
      }
      if (trackedErrorRef.current === input.error) {
        return;
      }
      trackedErrorRef.current = input.error;
      const sessionId = input.getSessionId();
      if (!sessionId) {
        return;
      }
      input.analytics[AI_SEARCH_EVENTS.REFUSAL]({
        block_reason: classifyRefusalFromError(input.error),
        session_id: sessionId
      });
    };
    $[0] = input;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== input.analytics || $[3] !== input.error) {
    t1 = [input.error, input.analytics];
    $[2] = input.analytics;
    $[3] = input.error;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  useEffect(t0, t1);
}